/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Guardian Registration Page - Create guardian account with family organization
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, User, Loader2, AlertCircle, Calendar, Info, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/authStore'
import { getErrorMessage } from '../utils/errorUtils.js'
import { handleEmailVerificationRequired } from '../utils/registrationUtils'
import AuthLayout from '../components/auth/AuthLayout'
import MarketingPanel from '../components/auth/MarketingPanel'
import FormInput from '../components/auth/FormInput'
import PasswordInput from '../components/auth/PasswordInput'
import SocialAuthButtons from '../components/auth/SocialAuthButtons'
import { createLogger } from '../utils/logger'

const logger = createLogger('RegisterGuardian')

const RegisterGuardian = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    dateOfBirth: '',
    ageConfirmed: false,
    termsAccepted: false,
    privacyAccepted: false
  })
  const [errors, setErrors] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [loadingApproval, setLoadingApproval] = useState(false)
  const [approvalDetails, setApprovalDetails] = useState(null)
  const [invitationData, setInvitationData] = useState(null)
  const [loadingInvitation, setLoadingInvitation] = useState(false)
  const [invitationToken, setInvitationToken] = useState(null)
  const [searchParams] = useSearchParams()
  const approvalToken = searchParams.get('approvalToken')

  const { register, user, checkAuth } = useAuthStore()
  const navigate = useNavigate()

  // Get referral code from URL (if present)
  const referralCode = searchParams.get('ref')

  // Pre-fill email from query parameter (used by email invite flow)
  useEffect(() => {
    const emailFromUrl = searchParams.get('email')
    if (emailFromUrl) {
      setFormData((prev) => ({
        ...prev,
        email: emailFromUrl
      }))
    }
  }, [searchParams])

  // Check for invitation token in URL
  useEffect(() => {
    const token = searchParams.get('token')
    if (token) {
      setInvitationToken(token)
      loadInvitationData(token)
    }
  }, [searchParams])

  // Fetch approval request details if coming from approval link
  useEffect(() => {
    if (approvalToken) {
      fetchApprovalDetails()
    }
  }, [approvalToken])

  const loadInvitationData = async (token) => {
    setLoadingInvitation(true)
    try {
      const response = await fetch(`/api/invitations/${token}`)

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Invalid invitation')
      }

      const result = await response.json()
      const invitation = result.data || result
      setInvitationData(invitation)

      // Prefill email from invitation
      setFormData((prev) => ({
        ...prev,
        email: invitation.email
      }))
    } catch (error) {
      logger.error('Error loading invitation:', error)
      toast.error(error.message || 'Failed to load invitation')
    } finally {
      setLoadingInvitation(false)
    }
  }

  const fetchApprovalDetails = async () => {
    try {
      setLoadingApproval(true)
      const response = await fetch(`/api/guardian-approval/${approvalToken}`)
      const data = await response.json()

      if (response.ok && data.success && data.approval) {
        // Store approval details for display
        setApprovalDetails(data.approval)

        // Prepopulate the guardian email from the approval request
        setFormData((prev) => ({
          ...prev,
          email: data.approval.guardianEmail
        }))
      } else {
        logger.error('Failed to fetch approval details:', data)
        toast.error('Could not load approval details')
      }
    } catch (error) {
      logger.error('Error fetching approval details:', error)
      toast.error('Failed to load approval information')
    } finally {
      setLoadingApproval(false)
    }
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }))

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }))
    }

    // Real-time password match validation
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      if (name === 'confirmPassword') {
        if (value && value !== formData.password) {
          setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
        } else {
          setErrors((prev) => ({ ...prev, confirmPassword: '' }))
        }
      } else if (name === 'password') {
        if (formData.confirmPassword && value !== formData.confirmPassword) {
          setErrors((prev) => ({ ...prev, confirmPassword: 'Passwords do not match' }))
        } else {
          setErrors((prev) => ({ ...prev, confirmPassword: '' }))
        }
      }
    }

    // Real-time age validation for guardians (must be 18+)
    if (name === 'dateOfBirth' && value) {
      const birthDate = new Date(value)
      const today = new Date()
      const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))

      if (age < 18) {
        setErrors((prev) => ({
          ...prev,
          dateOfBirth:
            'You must be at least 18 years old to create a guardian account. If you are a student, please use the student registration instead.'
        }))
      } else if (age > 120) {
        setErrors((prev) => ({ ...prev, dateOfBirth: 'Please enter a valid date of birth' }))
      } else {
        setErrors((prev) => ({ ...prev, dateOfBirth: '' }))
      }
    }
  }

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: checked
    }))

    // Clear error for this field when user checks
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // First name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required'
    }

    // Last name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address'
    }

    // Date of birth validation
    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required'
    } else {
      const birthDate = new Date(formData.dateOfBirth)
      const today = new Date()

      if (birthDate >= today) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth'
      }

      // Check if guardian is at least 18 years old
      const age = Math.floor((today - birthDate) / (365.25 * 24 * 60 * 60 * 1000))
      if (age < 18) {
        newErrors.dateOfBirth = 'Guardian must be at least 18 years old'
      } else if (age > 120) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth'
      }
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters'
    }

    // Confirm password validation
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    // Age confirmation validation
    if (!formData.ageConfirmed) {
      newErrors.ageConfirmed = 'You must confirm you are 18 years of age or older'
    }

    // Terms validation
    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the Terms of Service'
    }

    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = 'You must accept the Privacy Policy'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      toast.error('Please fix the errors in the form')
      return
    }

    setIsLoading(true)

    try {
      const { confirmPassword, ...registerData } = formData
      registerData.accountType = 'guardian'
      registerData.ageConfirmedAt = new Date().toISOString()

      // Include approval token if present (allows registration when signups are disabled)
      if (approvalToken) {
        registerData.approvalToken = approvalToken
      }

      // Include invitation token if present
      if (invitationToken) {
        registerData.invitationToken = invitationToken
      }

      // Include referral code if present
      if (referralCode) {
        registerData.referralCode = referralCode
      }

      const result = await register(registerData)

      if (result.success) {
        // Check if email verification is required
        if (handleEmailVerificationRequired(result, formData.email, navigate)) {
          return
        }

        // If there's an approval token, complete the child approval process
        if (approvalToken && result.user) {
          try {
            // Fetch the guardian's family organization ID
            const orgResponse = await fetch('/api/family/organization')
            const orgData = await orgResponse.json()

            if (!orgResponse.ok || !orgData.organization) {
              throw new Error('Could not find family organization')
            }

            // Now approve the child with the guardian's info
            const approvalResponse = await fetch(
              `/api/guardian-approval/${approvalToken}/approve`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                  guardianUserId: result.user.id,
                  familyOrganizationId: orgData.organization.id
                })
              }
            )

            const approvalData = await approvalResponse.json()

            if (approvalResponse.ok && approvalData.success) {
              toast.success(
                'Welcome! Your account has been created and your child has been approved.'
              )
              // Refresh session to ensure guardian status is fully loaded before navigation
              await checkAuth()
              navigate('/dashboard', { state: { freshLogin: true } })
            } else {
              // Account created but approval failed
              logger.error('Approval failed:', approvalData)
              toast.warning(
                'Your account was created, but there was an issue approving your child. Please contact support.'
              )
              // Refresh session to ensure guardian status is fully loaded before navigation
              await checkAuth()
              navigate('/dashboard', { state: { freshLogin: true } })
            }
          } catch (approvalError) {
            logger.error('Approval error:', approvalError)
            toast.warning(
              'Your account was created, but there was an issue approving your child. Please contact support.'
            )
            // Refresh session to ensure guardian status is fully loaded before navigation
            await checkAuth()
            navigate('/dashboard', { state: { freshLogin: true } })
          }
        } else {
          toast.success('Welcome to Deaveris! Your family account has been created.')
          // Refresh session to ensure guardian status is fully loaded before navigation
          await checkAuth()
          navigate('/dashboard', { state: { freshLogin: true } })
        }
      } else {
        throw new Error(getErrorMessage(result.error, 'Registration failed'))
      }
    } catch (error) {
      logger.error('Registration error:', error)
      toast.error(error.message || 'Registration failed. Please try again.')

      // Clear password fields on error
      setFormData((prev) => ({
        ...prev,
        password: '',
        confirmPassword: ''
      }))

      setTimeout(() => {
        document.getElementById('password')?.focus()
      }, 100)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout
      marketingContent={<MarketingPanel />}
      formTitle="Create Guardian Account"
      formSubtitle="Manage your family's SAT prep journey"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info Banner */}
        {approvalDetails ? (
          <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-green-900 dark:text-green-100 mb-1">
                  Approving Account for {approvalDetails.child?.name}
                </p>
                <p className="text-xs text-green-700 dark:text-green-300">
                  You're creating a guardian account to approve{' '}
                  <strong>{approvalDetails.child?.name}</strong>'s ({approvalDetails.child?.email})
                  student account. After completing registration, their account will be
                  automatically activated and added to your family organization.
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 mb-1">
                  Family Account Benefits
                </p>
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  As a guardian, you'll be able to invite up to 2 children, view their progress, and
                  manage your family's subscription. A family organization will be automatically
                  created for you.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Invitation Status Banner */}
        {loadingInvitation && (
          <div className="bg-primary-50 border border-primary-200 rounded-xl p-4 flex items-center gap-3">
            <Loader2 className="w-5 h-5 text-primary-600 animate-spin" />
            <span className="text-sm text-primary-700">Loading invitation...</span>
          </div>
        )}

        {invitationData && !approvalDetails && (
          <div className="bg-success-50 border border-success-200 rounded-xl p-4 space-y-2">
            <div className="flex items-center gap-2 text-success-700">
              <CheckCircle className="w-5 h-5" />
              <span className="text-sm font-semibold">Invitation verified</span>
            </div>
            <p className="text-sm text-success-600 pl-7">Email: {invitationData.email}</p>
            {invitationData.organization && (
              <p className="text-sm text-success-600 pl-7">
                Organization: {invitationData.organization.name}
              </p>
            )}
          </div>
        )}

        {/* Social Authentication */}
        {/* <SocialAuthButtons loading={isLoading} disabled={isLoading} /> */}

        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-4">
          <FormInput
            id="firstName"
            name="firstName"
            type="text"
            label="First Name"
            value={formData.firstName}
            onChange={handleChange}
            error={errors.firstName}
            required
            autoComplete="given-name"
            icon={<User className="w-5 h-5" />}
            disabled={isLoading}
          />
          <FormInput
            id="lastName"
            name="lastName"
            type="text"
            label="Last Name"
            value={formData.lastName}
            onChange={handleChange}
            error={errors.lastName}
            required
            autoComplete="family-name"
            disabled={isLoading}
          />
        </div>

        {/* Email Input */}
        <FormInput
          id="email"
          name="email"
          type="email"
          label="Email Address"
          value={formData.email}
          onChange={handleChange}
          error={errors.email}
          required
          autoComplete="email"
          icon={<Mail className="w-5 h-5" />}
          disabled={isLoading || loadingInvitation || !!approvalToken || !!invitationData}
          readOnly={!!invitationData || !!approvalToken}
          success={!!invitationData}
        />
        {approvalToken && (
          <p className="text-xs text-gray-500 dark:text-gray-400 -mt-4 ml-1">
            Email is locked because you're creating an account from a child approval request
          </p>
        )}

        {/* Date of Birth Input */}
        <div className="space-y-2">
          <FormInput
            id="dateOfBirth"
            name="dateOfBirth"
            type="date"
            label="Date of Birth"
            value={formData.dateOfBirth}
            onChange={handleChange}
            error={errors.dateOfBirth}
            required
            icon={<Calendar className="w-5 h-5" />}
            disabled={isLoading}
            max={new Date().toISOString().split('T')[0]}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
            We verify your age to ensure you can legally provide parental consent for minors under
            child protection laws (COPPA). Guardian accounts require users to be 18 or older. This
            information is securely stored.{' '}
            <a
              href="/legal/privacy"
              className="text-primary-600 dark:text-primary-400 hover:underline"
            >
              Learn more
            </a>
          </p>
        </div>

        {/* Password Fields */}
        <PasswordInput
          id="password"
          name="password"
          label="Password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          required
          autoComplete="new-password"
          disabled={isLoading}
          showStrengthMeter={true}
        />

        <PasswordInput
          id="confirmPassword"
          name="confirmPassword"
          label="Confirm Password"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
          required
          autoComplete="new-password"
          disabled={isLoading}
          showStrengthMeter={false}
        />

        {/* Age Confirmation and Legal Agreements */}
        <div className="space-y-4 pt-2 border-t border-gray-200 dark:border-gray-700">
          {/* Age Confirmation */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="ageConfirmed"
                checked={formData.ageConfirmed}
                onChange={handleCheckboxChange}
                disabled={isLoading}
                className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                I confirm that I am 18 years of age or older
              </span>
            </label>
            {errors.ageConfirmed && (
              <div className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-7">
                <AlertCircle className="w-3 h-3" />
                <span>{errors.ageConfirmed}</span>
              </div>
            )}
          </div>

          {/* Terms Acceptance */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="termsAccepted"
                checked={formData.termsAccepted}
                onChange={handleCheckboxChange}
                disabled={isLoading}
                className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                I accept the{' '}
                <Link
                  to="/legal/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </Link>
              </span>
            </label>
            {errors.termsAccepted && (
              <div className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-7">
                <AlertCircle className="w-3 h-3" />
                <span>{errors.termsAccepted}</span>
              </div>
            )}
          </div>

          {/* Privacy Acceptance */}
          <div className="space-y-2">
            <label className="flex items-start gap-3 cursor-pointer group">
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={formData.privacyAccepted}
                onChange={handleCheckboxChange}
                disabled={isLoading}
                className="mt-0.5 w-4 h-4 text-primary-600 bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-primary-500 focus:ring-offset-0 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              />
              <span className="flex-1 text-sm text-gray-700 dark:text-gray-300 select-none group-hover:text-gray-900 dark:group-hover:text-gray-100 transition-colors">
                I accept the{' '}
                <Link
                  to="/legal/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium underline underline-offset-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </Link>
              </span>
            </label>
            {errors.privacyAccepted && (
              <div className="flex items-center gap-1.5 text-xs text-error-600 dark:text-error-400 pl-7">
                <AlertCircle className="w-3 h-3" />
                <span>{errors.privacyAccepted}</span>
              </div>
            )}
          </div>
        </div>

        {/* Submit Button */}
        <motion.button
          type="submit"
          disabled={
            isLoading ||
            !formData.ageConfirmed ||
            !formData.termsAccepted ||
            !formData.privacyAccepted ||
            errors.dateOfBirth
          }
          whileHover={{
            scale:
              isLoading ||
              !formData.ageConfirmed ||
              !formData.termsAccepted ||
              !formData.privacyAccepted ||
              errors.dateOfBirth
                ? 1
                : 1.01
          }}
          whileTap={{
            scale:
              isLoading ||
              !formData.ageConfirmed ||
              !formData.termsAccepted ||
              !formData.privacyAccepted ||
              errors.dateOfBirth
                ? 1
                : 0.99
          }}
          className="w-full flex items-center justify-center px-4 py-2.5 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              <span>Creating guardian account...</span>
            </>
          ) : (
            'Create Guardian Account'
          )}
        </motion.button>

        {/* Sign In Link */}
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 transition-colors"
            >
              Sign in
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  )
}

export default RegisterGuardian
